import { darkColors, lightColors } from '@pancakeswap/ui/tokens/colors'
import { useId, useMemo } from 'react'
import styled, { css, useTheme, type DefaultTheme } from 'styled-components'
import { padlockSvg, type PadlockTone } from '../art/padlockSvg'

/*
 * The lock state badge (Bez, 2026-10-09: "lock and unlock must each have a badge. Unlocked is that same badge, switched off. Locked is a
 * badge that feels safe and verified-locked"). ONE shape for every state, so the state reads at a glance and nothing shifts between
 * states: the CADINU Lock padlock (art/padlockSvg.ts, round 2 of the Lock redesign) small, in a ring, then the label.
 *
 *   locked      closed padlock, teal glow ring, a small check seal       "Locked · 97 %"   (+ "until 2026-10-09" where there is room)
 *   partial     the same closed padlock, amber ring, no seal            "Partly locked · 12 %"   (art tone `partial`, its own)
 *   due         the padlock OPEN, amber ring, no seal, not dimmed       "Ready to unlock"   (a lock past its date: the owner can withdraw it
 *                                                                       at any moment, so it protects nothing; r3, Bez 2026-10-09)
 *   unlocked    the padlock OPEN, greyscale and dim; the label readable  "Not locked"
 *   checking /  neutral: a dashed ring and no padlock, so no claim       "Checking LP lock…" / "Lock status unavailable"
 *   unavailable
 *
 * The look carries the feeling; the words never say safe, audited or guaranteed. Who decides the state is the caller's existing logic: the
 * competition badge (TradingCompetitionV3/components/LockBadge.tsx), the lock surfaces (ui.tsx LockStatus / lockBadgeOf: explorer rows, the
 * certificate chip, the home's recent locks) and the token verdict (logic/groups liquidityVerdict). The check seal is earned, not given: only
 * the locked state can carry it, and a caller whose "locked" is not a verified share (a token lock, a pool under 50 %) passes check={false},
 * which also drops "verified" from the accessible name. The label sits on its own opaque tint of the card colour, so its contrast is known in
 * both themes (>= 4.5:1). A surface that is dark in both themes (the home's panels) passes `dark`: the badge then uses the dark tokens whatever
 * the theme, so it never draws a light chip on a dark panel.
 */

export type LockBadgeState = 'locked' | 'partial' | 'due' | 'unlocked' | 'checking' | 'unavailable'
type Ring = 'teal' | 'amber' | 'grey' | 'neutral'

const RING: Record<Ring, string> = { teal: '#3ddcb4', amber: '#f5b453', grey: '#7c86ab', neutral: '#7c86ab' }
const CHECK = '#39d98a'

const LOOK: Record<LockBadgeState, { ring: Ring; padlock: 'closed' | 'open' | 'none'; tone?: PadlockTone; check: boolean; tint: number }> = {
  locked: { ring: 'teal', padlock: 'closed', tone: 'locked', check: true, tint: 0.14 },
  // partial and due have separate art tones: what a due lock draws (open) can never change the partial look (closed)
  partial: { ring: 'amber', padlock: 'closed', tone: 'partial', check: false, tint: 0.12 },
  due: { ring: 'amber', padlock: 'open', tone: 'due', check: false, tint: 0.12 },
  unlocked: { ring: 'grey', padlock: 'open', tone: 'released', check: false, tint: 0.08 },
  checking: { ring: 'neutral', padlock: 'none', check: false, tint: 0.06 },
  unavailable: { ring: 'neutral', padlock: 'none', check: false, tint: 0.06 },
}

const pct = (bps?: number | null) => `${Math.round((bps ?? 0) / 100)} %`
const day = (t: number) => new Date(t * 1000).toISOString().slice(0, 10)

/** The words of a state. A caller with its own vocabulary (a lock's phase: "Releasing", "Ready to unlock") passes `label` instead. */
export function lockBadgeLabel(state: LockBadgeState, o: { shareBps?: number | null; manual?: boolean } = {}): string {
  if (state === 'locked') {
    if (o.manual) return 'Locked · checked by hand'
    return typeof o.shareBps === 'number' ? `Locked · ${pct(o.shareBps)}` : 'Locked'
  }
  if (state === 'partial') return `Partly locked · ${pct(o.shareBps)}`
  if (state === 'due') return 'Ready to unlock'
  if (state === 'unlocked') return 'Not locked'
  if (state === 'checking') return 'Checking LP lock…'
  return 'Lock status unavailable'
}

const hex = (c: string): [number, number, number] | null => {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c.trim())
  if (!m) return null
  const h = m[1].length === 3 ? m[1].replace(/./g, (x) => x + x) : m[1]
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}
const toHex = (rgb: number[]) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`
/** `top` at `alpha` laid over `base`, as an opaque #rrggbb */
const over = (top: string, alpha: number, base: string) => {
  const a = hex(top)
  const b = hex(base)
  if (!a || !b) return base
  return toHex(a.map((v, i) => v * alpha + b[i] * (1 - alpha)))
}

/**
 * The label colour and the badge's own opaque background in a theme: the pair the contrast rule is about. The theme's colours are CSS variables,
 * so the badge reads the token values themselves (@pancakeswap/ui/tokens/colors) for the theme it is drawn in.
 */
export function badgeColors(state: LockBadgeState, theme: Pick<DefaultTheme, 'isDark'>) {
  const p = theme.isDark ? darkColors : lightColors
  const strong = state === 'locked' || state === 'partial' || state === 'due'
  return { text: strong ? p.text : p.textSubtle, background: over(RING[LOOK[state].ring], LOOK[state].tint, p.backgroundAlt) }
}

/** the dark tokens, for a surface that is dark in both themes */
const DARK_SURFACE: Pick<DefaultTheme, 'isDark'> = { isDark: true }
const colorsOn = (state: LockBadgeState, theme: Pick<DefaultTheme, 'isDark'>, dark?: boolean) =>
  badgeColors(state, dark ? DARK_SURFACE : theme)

const Root = styled.span<{ $ring: string; $state: LockBadgeState; $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  max-width: 100%;
  padding: 0 12px 0 2px;
  border-radius: 999px;
  vertical-align: middle;
  white-space: nowrap;
  font-size: 13px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $state, $dark }) => colorsOn($state, theme, $dark).text};
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0) 55%),
    ${({ theme, $state, $dark }) => colorsOn($state, theme, $dark).background};
  border: 1px solid
    ${({ $ring, $state }) => `${$ring}${$state === 'locked' ? 'a6' : $state === 'partial' ? '8c' : $state === 'due' ? '73' : '59'}`};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08)
    ${({ $ring, $state }) => ($state === 'locked' ? `, 0 0 14px -3px ${$ring}99` : '')};
  ${({ $state }) =>
    $state === 'unlocked' || $state === 'checking' || $state === 'unavailable'
      ? css`
          font-weight: 600;
        `
      : ''}
`
const Emblem = styled.span<{ $ring: string; $state: LockBadgeState }>`
  position: relative;
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 38%, ${({ $ring }) => `${$ring}47`} 0%, rgba(11, 16, 40, 0) 72%), #0b1028;
  border: 1.5px ${({ $state }) => ($state === 'checking' || $state === 'unavailable' ? 'dashed' : 'solid')}
    ${({ $ring, $state }) => ($state === 'unlocked' ? `${$ring}b3` : $ring)};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16)
    ${({ $ring, $state }) => ($state === 'locked' ? `, 0 0 9px ${$ring}b3` : $state === 'partial' ? `, 0 0 6px ${$ring}66` : '')};
  .art {
    display: block;
    width: 20px;
    height: 22px;
    margin-top: -1px;
    line-height: 0;
  }
  .art svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
  &[data-dim='true'] .art {
    filter: grayscale(1) brightness(1.05);
    opacity: 0.7;
  }
  /* at 20 px an open padlock's lift is about one pixel: swing the shackle out on its left leg so "open" reads at a glance (due, unlocked) */
  &[data-open='true'] .art .shackle {
    transform-box: fill-box;
    transform-origin: 0% 100%;
    transform: rotate(-22deg);
  }
`
const Seal = styled.span`
  position: absolute;
  right: -4px;
  bottom: -3px;
  display: inline-grid;
  place-items: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${CHECK};
  box-shadow: 0 0 0 1.5px #0b1028, 0 0 6px ${CHECK}99;
  svg {
    display: block;
    width: 10px;
    height: 10px;
  }
`
const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`
const Until = styled.span<{ $ring: string }>`
  padding-left: 8px;
  border-left: 1px solid ${({ $ring }) => `${$ring}66`};
  font-size: 12px;
  font-weight: 600;
`

/** checking: three dots; unavailable: a dash. Neither is a padlock, so neither claims anything. */
function NeutralMark({ state, color }: { state: LockBadgeState; color: string }) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      {state === 'checking' ? (
        <g fill={color}>
          <circle cx="3.5" cy="8" r="1.6" />
          <circle cx="8" cy="8" r="1.6" />
          <circle cx="12.5" cy="8" r="1.6" />
        </g>
      ) : (
        <path d="M4 8 H12" stroke={color} strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  )
}

export type LockStateBadgeProps = {
  state: LockBadgeState
  /** the live locked share, in basis points: "Locked · 97 %", "Partly locked · 12 %" */
  shareBps?: number | null
  /** an admin approved a hand check of a lock elsewhere: "Locked · checked by hand" */
  manual?: boolean
  /** the until-date (unix seconds), shown where the caller has room */
  until?: number | null
  /** the caller's own words for the state (a lock's phase); the look still follows `state` */
  label?: string
  /** the check seal (and "verified" in the accessible name): only a locked badge can carry it; false draws the locked look without it */
  check?: boolean
  /** the surface under the badge is dark in both themes (the home's panels): use the dark tokens whatever the theme */
  dark?: boolean
  testid?: string
  className?: string
} & Omit<React.HTMLAttributes<HTMLSpanElement>, 'children' | 'role'>

export default function LockStateBadge({ state, shareBps, manual, until, label, check, dark, testid, className, ...rest }: LockStateBadgeProps) {
  const look = LOOK[state]
  const theme = useTheme() as DefaultTheme | undefined
  const sealed = look.check && check !== false
  const ring = RING[look.ring]
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const text = label ?? lockBadgeLabel(state, { shareBps, manual })
  const untilText = until ? `until ${day(until)}` : ''
  // the medal's ring inside the padlock carries the locked share (a full ring when the share is not known)
  // a due lock has served all its time: a full amber ring
  const progress =
    state === 'unlocked' ? 0 : state === 'due' ? 1 : typeof shareBps === 'number' ? Math.min(1, Math.max(0, shareBps / 10000)) : 1
  const svg = useMemo(
    () => (look.tone ? padlockSvg({ progress, tone: look.tone, uid: `b${uid}`, mini: true }) : ''),
    [look.tone, progress, uid],
  )
  const name = `Lock status: ${[...text.split(' · '), untilText].filter(Boolean).join(', ')}${sealed ? ', verified' : ''}`
  return (
    <Root
      role="img"
      aria-label={name}
      data-testid={testid ?? 'lock-state'}
      data-shape="lock-badge"
      data-state={state}
      data-padlock={look.padlock}
      data-ring={look.ring}
      data-check={sealed ? 'true' : 'false'}
      data-scheme={dark || theme?.isDark ? 'dark' : 'light'}
      className={className}
      $ring={ring}
      $state={state}
      $dark={dark}
      {...rest}
    >
      <Emblem
        data-testid="lock-state-emblem"
        data-dim={state === 'unlocked' ? 'true' : undefined}
        data-open={look.padlock === 'open' ? 'true' : undefined}
        $ring={ring}
        $state={state}
      >
        {svg ? (
          // built from numbers and fixed colours only (art/padlockSvg.ts), never from a lock's text
          <span className="art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />
        ) : (
          <NeutralMark state={state} color="#aab3d1" />
        )}
        {sealed ? (
          <Seal data-testid="lock-state-check" aria-hidden="true">
            <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false">
              <path d="M2.3 5.2 4.2 7.1 7.8 3.2" fill="none" stroke="#04220f" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Seal>
        ) : null}
      </Emblem>
      <Label data-testid="lock-state-label">{text}</Label>
      {untilText ? (
        <Until data-testid="lock-state-until" $ring={ring}>
          {untilText}
        </Until>
      ) : null}
    </Root>
  )
}
